import type { ConnectError } from '@connectrpc/connect'
import {
  DemandSuppressionMasterErrorReason,
  DemandSuppressionMasterErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  type AppErrorInfoResolution,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'

/**
 * `DemandSuppressionMasterErrorReason`
 * (occto-plan-master-reason-registry 設計書, backend `occtomaster` domain)
 * → i18n key + field routing registry for the DemandSuppressionMaster
 * Create/Update modals (mirrors BgMaster/models/serverError.ts, decision
 * E). See that file's `BG_MASTER_REASON_KEYS` godoc for the `Record<Enum,
 * AppErrorInfoReasonEntry | undefined>` compile-time-coverage contract and
 * the `field` routing rationale (docs/adr/
 * structured-app-error-standard-errorinfo-envelope.md decision 2).
 *
 * D3/D4: `SUBMITTER_NOT_FOUND` (計画提出者 select) is the only
 * field-scoped entry (field=`submitterMasterId`); every other reason
 * (license mismatch / suppression-BG / partner-BG / business / duplicate)
 * is operation-scoped. D6: keys live under this tab's own
 * `serverError.reason.*` sub-namespace to disambiguate reason names that
 * also exist on the GSP tab (both have `submitterNotFound` /
 * `submitterLicenseMismatch`).
 */
const DEMAND_SUPPRESSION_MASTER_REASON_KEYS: Record<
  DemandSuppressionMasterErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [DemandSuppressionMasterErrorReason.UNSPECIFIED]: undefined,
  [DemandSuppressionMasterErrorReason.SUBMITTER_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.reason.submitterNotFound',
    field: 'submitterMasterId',
  },
  [DemandSuppressionMasterErrorReason.SUBMITTER_LICENSE_MISMATCH]: {
    key: 'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.reason.submitterLicenseMismatch',
  },
  [DemandSuppressionMasterErrorReason.SUPPRESSION_BG_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.reason.suppressionBgNotFound',
  },
  [DemandSuppressionMasterErrorReason.SUPPRESSION_BG_LICENSE_MISMATCH]: {
    key: 'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.reason.suppressionBgLicenseMismatch',
  },
  [DemandSuppressionMasterErrorReason.PARTNER_BG_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.reason.partnerBgNotFound',
  },
  [DemandSuppressionMasterErrorReason.BUSINESS_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.reason.businessNotFound',
  },
  // AC-OMD-001a: reuses the pre-existing spec-worded duplicate key (the
  // dynamic `{{submitterCode}}` interpolation comes from the resolver's
  // `values` argument, D1) rather than minting a second copy of the same
  // text under `serverError.reason.*`.
  [DemandSuppressionMasterErrorReason.SUBMITTER_ALREADY_HAS_SUPPRESSION_MASTER]:
    {
      key: 'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.duplicateSubmitterSuppressionPlan',
    },
}

/**
 * `resolveDetailedError` for DemandSuppressionMaster's `useCrudModalForm`
 * (Create / Update). Resolves the `google.rpc.ErrorInfo` Connect error
 * detail, if any, against `DEMAND_SUPPRESSION_MASTER_REASON_KEYS` via the
 * `DemandSuppressionMasterErrorReasonSchema` reflection lookup. `values`
 * is passed through to `t(key, values)` for the duplicate reason's
 * `{{submitterCode}}` interpolation (D1); callers must source it from the
 * fresh submitted form values (see useCrudModalForm's resolveDetailedError
 * contract, N3).
 */
export function resolveDemandSuppressionMasterDetailedError(
  err: ConnectError,
  t: TFunction,
  values: Record<string, unknown>,
): AppErrorInfoResolution | undefined {
  return resolveAppErrorInfoMessage(
    err,
    DemandSuppressionMasterErrorReasonSchema,
    DEMAND_SUPPRESSION_MASTER_REASON_KEYS,
    t,
    values,
  )
}
